import React, { FC, useContext, useEffect, useMemo, useState, useCallback } from "react";
import "./TaskChecklistGroup.css";

import EditColumnGray from "../../../assets/editButtonGray.svg";
import ExitCross from "../../../assets/cancelGreey.svg";
import DeleteElementGray from "../../../assets/deleteElementGreyTrue.svg";
import arrowDown from "../../../assets/arrowDown.svg";

import { AppContext } from "../../../App";
import { Context } from "../../../index";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import TaskChecklistElements from "../TaskChecklistElements/TaskChecklistElements";
import SaveInputValue from "../../../assets/save_input_value.svg";
import axios from "axios";
import ConfirmationWindow from "../../Shared/ConfirmationWindow/ConfirmationWindow";
import { hasPermission } from "../../../helpers/hasPermisson";
import { IOptionsQueryParams } from "../../../helpers/paramsAutoFiller";
import { ITaskChecklistEdit } from "../../../common/api_interfaces/taskChecklist/ITaskChecklistEdit";
import { ITaskChecklistGroup } from "../../../common/api_interfaces/taskChecklist/ITaskChecklistGroup";

import { ReactComponent as DragDropGray } from "../../../assets/dragDrop.svg";
import { ReactComponent as AddDefault } from "../../../assets/images/addDefault.svg";
import Button from "../../UiLib/Button/Button";

interface handleEditChecklistGroup {
    checklistGroupId: number;
    newName?: string;
    newPosition?: number;
}

interface handleEditChecklistElement {
    checklistElemntId: number;
    newText?: string;
    newPosition?: number;
    newChecked?: boolean;
}

interface ITaskChecklistGroupProps {
    checklistGroup: ITaskChecklistGroup;
    onChecklistGroupDragEnd: () => void;
    draggable: boolean;
    deletable: boolean;
    systemUserAccess: IFunctionPermissions[];
    userAccess: IFunctionPermissions[];
    editGroup: (handleEditChecklistGroup: handleEditChecklistGroup) => Promise<void>;
    loadChecklist: () => Promise<void>;
    createElement?: (groupId?: number, nameElement?: string) => Promise<void>;
    openAllDropdown?: boolean;
    setIsLowerElementDrag: React.Dispatch<React.SetStateAction<boolean>>;
    setIsUpperElementDrag: React.Dispatch<React.SetStateAction<boolean>>;
    isUpperElementDrag: boolean;
    setChecklistGroup: React.Dispatch<React.SetStateAction<ITaskChecklistGroup | undefined>>;
    showElementsList: (e: number) => void;
    showElementsArr: number[];
    optionsQueryParams: IOptionsQueryParams;
}

const TaskChecklistGroup: FC<ITaskChecklistGroupProps> = ({
    setChecklistGroup,
    setIsUpperElementDrag,
    setIsLowerElementDrag,
    checklistGroup,
    onChecklistGroupDragEnd,
    draggable,
    deletable,
    systemUserAccess,
    userAccess,
    editGroup,
    loadChecklist,
    createElement,
    openAllDropdown,
    isUpperElementDrag,
    showElementsList,
    showElementsArr,
    optionsQueryParams,
}) => {
    const [renameMode, setRenameMode] = useState<boolean>(false);
    const { showToast } = useContext(AppContext);
    const { store } = useContext(Context);
    const [openConfirmWindow, setOpenConfirmWindow] = useState<boolean>(false);
    const [showGroupBtn, setShowGroupBtn] = useState<number>();
    const [showAddElementInput, setShowElementInput] = useState<boolean>(false);
    const [groupName, setGroupName] = useState<string>(checklistGroup.name || "");
    const [nameElement, setNameElement] = useState<string>("");
    const [dragEventTarget, setDragEventTarget] = useState<any>();

    const deleteChecklistGroup = useMemo(() => hasPermission(systemUserAccess, "ChecklistAction", "delete") || hasPermission(userAccess, "ChecklistAction", "delete"), [userAccess, systemUserAccess]);
    const editChecklistGroup = useMemo(() => hasPermission(systemUserAccess, "ChecklistAction", "edit") || hasPermission(userAccess, "ChecklistAction", "edit"), [userAccess, systemUserAccess]);
    const createChecklistGroup = useMemo(() => hasPermission(systemUserAccess, "ChecklistAction", "create") || hasPermission(userAccess, "ChecklistAction", "create"), [userAccess, systemUserAccess]);

    const isGroupVisible = useMemo(() =>
        openAllDropdown || showElementsArr.includes(checklistGroup.id) || checklistGroup.position === 0,
        [showElementsArr, checklistGroup.id, checklistGroup.position]
    );

    const sortedChecklist = useMemo(() =>
        checklistGroup.checklist?.sort((a, b) => a.position - b.position) || [],
        [checklistGroup.checklist]
    );

    const removePreview = useCallback(() => {
        const elements = document.querySelectorAll(".column-preview");
        elements.forEach((element) => element.remove());
    }, []);

    const onDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        setIsUpperElementDrag(true);

        setTimeout(() => {
            const columnElement = (e.target as HTMLElement).closest(".task_checklist_group");
            columnElement?.classList.add("drag-hidden");
        }, 0);

        e.dataTransfer.clearData();
        e.dataTransfer.setData(
            "application/json",
            JSON.stringify({ checklistGroupId: checklistGroup.id })
        );
    }, [checklistGroup.id, setIsUpperElementDrag]);

    const onDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        setIsUpperElementDrag(false);

        setTimeout(() => {
            const columnElement = (e.target as HTMLElement).closest(".task_checklist_group");
            columnElement?.classList.remove("drag-hidden");
        }, 100);

        onChecklistGroupDragEnd();
    }, [onChecklistGroupDragEnd, setIsUpperElementDrag]);

    const handleNameChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            editGroup({
                checklistGroupId: checklistGroup.id,
                newName: e.currentTarget.value,
            });
            setRenameMode(false);
        }
    }, [checklistGroup.id, editGroup]);

    const saveTextChange = useCallback(() => {
        if (groupName.trim()) {
            editGroup({
                checklistGroupId: checklistGroup.id,
                newName: groupName,
            });
            setRenameMode(false);
        }
    }, [checklistGroup.id, editGroup, groupName]);

    const createElementOnGroup = useCallback(async (e?: React.KeyboardEvent<HTMLInputElement>) => {
        if (e && e.key !== "Enter") return;
        if (!createElement || !nameElement.trim()) return;

        await createElement(checklistGroup.id, nameElement);
        setNameElement("");
        setShowElementInput(false);
    }, [createElement, checklistGroup.id, nameElement]);

    const handleDeleteChecklistGroup = useCallback(async () => {
        try {
            await store.taskService.deleteTaskChecklistGroup(Number(checklistGroup.id));
            await loadChecklist();
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [checklistGroup.id, loadChecklist, showToast, store.taskService]);

    const editElement = useCallback(async (data: handleEditChecklistElement) => {
        const checklistElement = checklistGroup.checklist?.find(xx => xx.id === data.checklistElemntId);
        if (!checklistElement) return;

        try {
            const Element: ITaskChecklistEdit = {
                id: data.checklistElemntId,
                text: data.newText,
                checked: data.newChecked,
                taskId: checklistGroup.taskId,
            };

            // Оптимистичное обновление
            if (data.newChecked !== undefined) checklistElement.checked = data.newChecked;
            if (data.newText !== undefined) checklistElement.text = data.newText;

            setChecklistGroup({ ...checklistGroup });

            await store.taskService.editTaskChecklistElement(Element);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
                // Откат изменений при ошибке
                await loadChecklist();
            }
        }
    }, [checklistGroup, setChecklistGroup, store.taskService, showToast, loadChecklist]);

    const showGroupsList = useCallback((id: number) => {
        setShowGroupBtn(id);
    }, []);

    const hideGroupsList = useCallback(() => {
        setShowGroupBtn(undefined);
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragEventTarget(e.target);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (dragEventTarget !== e.target) {
            removePreview();
        }
    }, [dragEventTarget, removePreview]);

    const addElementInputShow = useCallback(() => {
        setShowElementInput(prev => !prev);
        setNameElement("");
    }, []);

    const handleGroupNameClick = useCallback(() => {
        showElementsList(checklistGroup.id);
    }, [checklistGroup.id, showElementsList]);

    const handleEditButtonClick = useCallback(() => {
        if (!editChecklistGroup) {
            showToast("У вас нет прав для редактирования группы!");
            return;
        }
        setRenameMode(prev => !prev);
        setGroupName(checklistGroup.name || "");
    }, [editChecklistGroup, showToast, checklistGroup.name]);

    const handleDeleteButtonClick = useCallback(() => {
        if (!deleteChecklistGroup) {
            showToast("У вас нет прав для удаления группы!");
            return;
        }
        setOpenConfirmWindow(true);
    }, [deleteChecklistGroup, showToast]);

    const handleAddElementClick = useCallback(() => {
        if (!createChecklistGroup) {
            showToast("Вы не можете добавлять элементы чек листа пока у вас нет прав на редактирование задачи");
            return;
        }
        addElementInputShow();
    }, [createChecklistGroup, showToast, addElementInputShow]);

    return (
        <div className="task_checklist_group_container" id={`group-${checklistGroup.id}`}>
            {openConfirmWindow && (
                <ConfirmationWindow
                    headerText="Удаление группы"
                    bodyText="Вы уверены, что хотите удалить группу?"
                    confirmText="Да"
                    cancelText="Нет"
                    onConfirm={handleDeleteChecklistGroup}
                    onCancel={() => setOpenConfirmWindow(false)}
                />
            )}

            {draggable &&
                <div className="task_checklist_group">
                    <div
                        className="drag-drop-button"
                        draggable={draggable && editChecklistGroup}
                        onDragStart={editChecklistGroup ? onDragStart : undefined}
                        onDragEnd={onDragEnd}
                    >
                        {editChecklistGroup && draggable ? (
                            <DragDropGray className="drag-drop-edit"/>
                        ) : draggable ? (
                            <DragDropGray
                                onClick={() => showToast("У вас нет прав для перемещения группы!")}
                                className="drag-drop-edit"
                            />
                        ) : (
                            <div className="drag-drop-edit" />
                        )}
                    </div>

                    <div
                        className="task_checklist_group__block"
                        onMouseOver={() => showGroupsList(checklistGroup.id)}
                        onMouseOut={hideGroupsList}
                    >
                        <div className="task-checklist-add-button">
                            <Button
                                id={createChecklistGroup ? "button-cross" : "button-cross-gray"}
                                callBack={handleAddElementClick}
                                image={<AddDefault />}
                            />
                        </div>

                        <div 
                            className="task-checklist-arrow"
                            onClick={handleGroupNameClick}
                        >
                            <img
                                src={arrowDown}
                                alt="Toggle"
                                style={{
                                    transform: isGroupVisible ? "rotate(180deg)" : "none",
                                    cursor: "pointer",
                                }}
                            />
                        </div>

                        {renameMode ? (
                            <div className="task_checklist_group__block--name">
                                <input
                                    type="text"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    onKeyDown={handleNameChange}
                                    autoFocus
                                />
                            </div>
                        ) : (
                            <div className="task_checklist_group__block--name" onClick={handleGroupNameClick}>
                                {checklistGroup.name}
                            </div>
                        )}

                        {showGroupBtn === checklistGroup.id && (
                            <div className="task_checklist_group__block__button">
                                <button onClick={handleEditButtonClick}>
                                    <img src={renameMode ? ExitCross : EditColumnGray} alt={renameMode ? "Cancel" : "Edit"} />
                                </button>

                                {deletable && !renameMode && (
                                    <button onClick={handleDeleteButtonClick}>
                                        <img src={DeleteElementGray} alt="Delete" />
                                    </button>
                                )}

                                {renameMode && (
                                    <button
                                        className="postive-green-button"
                                        onClick={saveTextChange}
                                        disabled={!groupName.trim()}
                                    >
                                        <img src={SaveInputValue} alt="Save" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            }

            {showAddElementInput && (
                <div className="task_checklist_elem--input">
                    <div className="task_checklist_elem__block--name">
                        <input
                            type="text"
                            value={nameElement}
                            onChange={(e) => setNameElement(e.target.value)}
                            onKeyDown={createElementOnGroup}
                            placeholder="Введите название пункта"
                            autoFocus
                        />
                    </div>
                    <button
                        className="grey-cancel-button"
                        onClick={addElementInputShow}
                    >
                        <img src={ExitCross} alt="Cancel" />
                    </button>
                    <button
                        className="postive-green-button right-aligned"
                        onClick={() => createElementOnGroup()}
                        disabled={!nameElement.trim()}
                    >
                        <img src={SaveInputValue} alt="Save" />
                    </button>
                </div>
            )}

            {isGroupVisible && (
                <div
                    className="task_checklist_element"
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    id={`groupElement-${checklistGroup.id}`}
                >
                    {sortedChecklist.map((c) => (
                        <TaskChecklistElements
                            key={c.id}
                            setChecklistGroup={setChecklistGroup}
                            checkListGroup={checklistGroup}
                            setIsLowerElementDrag={setIsLowerElementDrag}
                            openAllDropdown={openAllDropdown}
                            checklistElement={c}
                            onChecklistElementDragEnd={removePreview}
                            draggable={true}
                            systemUserAccess={systemUserAccess}
                            userAccess={userAccess}
                            editElement={editElement}
                            loadChecklist={loadChecklist}
                            optionsQueryParams={{
                                ...optionsQueryParams,
                                cheklistId: c.id,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(TaskChecklistGroup);